'use client';
import { withTimeout } from '@s7/utils';
import { PluginsStatusTracker } from './PluginsStatusTracker/PluginsStatusTracker.js';
import { EventDeferrer } from './EventDeferrer/EventDeferrer.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
function _create_for_of_iterator_helper_loose(o, allowArrayLike) {
    var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
    if (it) return (it = it.call(o)).next.bind(it);
    if (Array.isArray(o) || (it = _unsupported_iterable_to_array(o)) || allowArrayLike && o && typeof o.length === "number") {
        if (it) o = it;
        var i = 0;
        return function() {
            if (i >= o.length) {
                return {
                    done: true
                };
            }
            return {
                done: false,
                value: o[i++]
            };
        };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
/**
 * Используется для защиты жизненного цикла от повторных вызовов
 * `resolve` / `reject` в возвращаемом Promise.
 *
 * ! Гарантирует, что жизненный цикл плагина не будет нарушен,
 * когда проблемы реализации инструмента аналитики
 * невозможно решить на уровне плагина.
 *
 * !Важно! Невозможно поместить в локальную область видимости.
 * */ var callsGuard = new Map();
/**
 * Отвечает за инициализацию состояния плагинов
 * и передачу событий плагинам для обработки.
 *
 * Если плагин инициализируется асинхронно - создаёт временную очередь событий.
 * Попытка асинхронной инициализации прекращается по таймауту.
 *
 * Порядок вызовов:
 *
 * init().listen();
 * */ var AnalyticsWorker = function(options) {
    var init = /**
     * [LIFECYCLE CALL] initialize
     *
     * Отвечает за инициализацию плагина.
     * */ function init() {
        var _loop = function() {
            var plugin = _step.value;
            callsGuard.set(plugin.id, false);
            var evaluator = plugin.initialize();
            var status = evaluator();
            tracker.track(plugin);
            if (_instanceof(status, Promise)) {
                withTimeout(status, timeout).then(function() {
                    if (!callsGuard.get(plugin.id)) {
                        callsGuard.set(plugin.id, true);
                        tracker.activate(plugin);
                        deferrer.dispatchDeferredEvents(plugin);
                    }
                }).catch(function() {
                    tracker.untrack(plugin);
                    deferrer.eject(plugin);
                });
            } else if (status) {
                tracker.activate(plugin);
            } else {
                tracker.untrack(plugin);
                deferrer.eject(plugin);
            }
        };
        for(var _iterator = _create_for_of_iterator_helper_loose(plugins), _step; !(_step = _iterator()).done;)_loop();
        return {
            listen: listen
        };
    };
    var listen = /**
     * [LIFECYCLE CALL] onEventEmitted
     * */ function listen() {
        bus.emit("worker-started-listening");
        bus.on("event-added", function(eventName, payload) {
            var _loop = function() {
                var plugin = _step.value;
                if (!tracker.isTracked(plugin)) {
                    return "continue";
                }
                if (tracker.isActive(plugin)) {
                    if (deferrer.hasLimitedQueueFor(plugin)) {
                        deferrer.enqueue(plugin, eventName, payload);
                    } else {
                        /*
                         * Во избежание потенциальной блокировки main thread
                         * клиентом аналитики оборачиваем в Promise.
                         * */ new Promise(function(resolve) {
                            return resolve(plugin.onEventEmitted(eventName, payload));
                        }).then(function() {
                            return bus.emit("event-sent", eventName, payload);
                        }).catch(function(error) {
                            return bus.emit("event-error", eventName, payload, error);
                        });
                    }
                } else {
                    deferrer.enqueue(plugin, eventName, payload);
                }
            };
            for(var _iterator = _create_for_of_iterator_helper_loose(plugins), _step; !(_step = _iterator()).done;)_loop();
        });
    };
    var bus = options.bus, plugins = options.plugins, _options_timeout = options.timeout, timeout = _options_timeout === void 0 ? 5 * 1000 : _options_timeout;
    var tracker = PluginsStatusTracker({
        bus: bus
    });
    var deferrer = EventDeferrer({
        bus: bus
    });
    var worker = {
        init: init
    };
    return worker;
};

export { AnalyticsWorker };
